/* eslint-disable max-classes-per-file */
import React, { Component } from 'react'
import { connect } from 'react-redux'
import ReactPlayer from 'react-player'
import { Animated, View, StyleSheet, Image, Pressable } from 'react-native'
import { isEqual } from 'lodash'

import { addImageLoaded } from 'ducks/images'
import { applyImgixParameters } from 'utils/imgix'
import PlayButton from 'assets/video_play.png'
import { getImageUrl } from 'utils/assets'
import { solveBindings } from 'utils/library-binding'
import BaseObject from './BaseObject'
import AppContext from '../contexts/AppContext'
import { createBindingUtils } from '../utils/bindingUtils'

const libraryComponentManifest = {
  props: [
    {
      name: 'video',
      displayName: 'Video',
      type: 'video',
    },
    // thumbnail image
    {
      name: 'image',
      displayName: 'Image',
      type: 'image',
    },
  ],
}

class VideoView extends BaseObject {
  static contextType = AppContext

  constructor(props) {
    super(props)

    this.state = {
      layout: null,
      videoLoadError: false,
      clickedPlay: false,
      thumbnailOpacity: new Animated.Value(0),
    }
  }

  componentDidUpdate(prevProps) {
    const { componentProps } = this.props

    if (!isEqual(componentProps, prevProps.componentProps)) {
      if (this.getURL() !== 'invalid_url') {
        this.setState({ videoLoadError: false, clickedPlay: false })
      }
    }
  }

  getURL = () => {
    const { object, componentProps } = this.props

    const getSuitableVideoUrl = () => {
      if (object.attributes.videoType === 'url') {
        if (componentProps && componentProps.video) {
          return componentProps.video.uri
        } else {
          return object.attributes.videoURL
        }
      }

      throw new Error('uploaded videos not supported, yet')
    }

    const videoUrl = getSuitableVideoUrl()

    // avoid returning a blank string
    // to force the video player onError callback to trigger
    return typeof videoUrl === 'string' && videoUrl.length > 0
      ? videoUrl
      : 'invalid_url'
  }

  getPlaceholderImage = () => {
    const { object, getAssetURL } = this.props

    if (
      object &&
      object.attributes &&
      object.attributes.videoBinding &&
      typeof object.attributes.videoBinding.options !== 'undefined'
    ) {
      const { options } = object.attributes.videoBinding
      const placeholderImageEnabled =
        typeof options.placeholderImageEnabled !== 'undefined'
          ? options.placeholderImageEnabled
          : null
      const placeholderImage =
        typeof options.placeholderImage !== 'undefined'
          ? options.placeholderImage
          : null
      if (placeholderImageEnabled && placeholderImage) {
        return getAssetURL(options.placeholderImage)
      }
    }

    return undefined
  }

  getHeight() {
    const { nodeHeight, isResponsiveComponent, object } = this.props
    const { layout } = object
    if (
      isResponsiveComponent &&
      object.children?.length &&
      nodeHeight !== undefined
    ) {
      return nodeHeight
    }
    return layout.height
  }

  onLayout = event => {
    if (!this.state.layout) {
      this.setState({ layout: event.nativeEvent.layout })
    }
  }

  render() {
    const { object, topScreen } = this.props

    if (!topScreen) {
      return null
    }

    const { attributes, layout } = object
    const resizeMode = attributes.imageResize || 'cover'
    const {
      paddingLeft,
      paddingRight,
      paddingTop,
      paddingBottom,
      ...layoutStyles
    } = layout

    const wrapperStyles = {
      ...layoutStyles,
      opacity: attributes.opacity,
      height: this.getHeight(),
    }

    const innerWrapperStyles = {
      paddingLeft,
      paddingRight,
      paddingTop,
      paddingBottom,
      height: layout.height,
    }

    const borderOffset = this.getBorderOffset()
    const borderStyles = this.borderStyles()
    const borderWidth = borderStyles.borderWidth || 0

    const borderPadding = 0.5 * borderWidth + borderOffset

    const backgroundStyles = {
      ...this.shadowStyles(),
      ...borderStyles,
      ...this.backgroundStyles(),
      left: -borderPadding,
      top: -borderPadding,
      right: -borderPadding,
      bottom: -borderPadding,
    }

    const backgroundOverlayInnerStyles = {
      flex: 1,
      margin: -borderPadding,
    }

    // Show pre-play overlay when:
    // - layout is available
    // - video auto play is false
    // - play button has not been clicked
    const thumbnailImage = getImageUrl(this.props, this.context)
    const thumbnail =
      this.state.layout &&
      attributes.videoAutoPlay === false &&
      this.state.clickedPlay === false ? (
        <Pressable
          style={[styles.pressableContainer]}
          onPress={() => this.setState({ clickedPlay: true })}
        >
          <Animated.View
            style={[
              styles.thumbnailContainer,
              { opacity: this.state.thumbnailOpacity },
            ]}
          >
            {typeof thumbnailImage === 'string' &&
              thumbnailImage.length > 0 && (
                <Image
                  source={applyImgixParameters(
                    { uri: thumbnailImage },
                    this.state.layout
                  )}
                  style={[
                    styles.thumbnailImage,
                    { borderRadius: attributes.borderRadius },
                  ]}
                  resizeMode={resizeMode}
                />
              )}
            <View style={styles.thumbnailPlayButtonContainer}>
              <Image
                source={PlayButton}
                style={styles.thumbnailPlayButton}
                resizeMode="contain"
              />
            </View>
          </Animated.View>
        </Pressable>
      ) : null

    if (thumbnail !== null) {
      Animated.timing(this.state.thumbnailOpacity, {
        toValue: 1,
        duration: 100,
        useNativeDriver: false,
      }).start()
    }

    // Show placeholder image when:
    // - layout is available
    // - video has errored
    // - play button has been clicked
    // - placeholder exists
    const placeholderImage = this.getPlaceholderImage()
    const placeholder =
      this.state.layout &&
      this.state.videoLoadError === true &&
      (this.state.clickedPlay === true || attributes.videoAutoPlay === true) &&
      typeof placeholderImage === 'string' &&
      placeholderImage.length > 0 ? (
        <Image
          source={applyImgixParameters(
            { uri: placeholderImage },
            this.state.layout
          )}
          style={[
            styles.placeholderImage,
            { borderRadius: attributes.borderRadius },
          ]}
          resizeMode={resizeMode}
        />
      ) : null

    const props = {
      config: {
        file: {
          attributes: {
            style: {
              ...(resizeMode === 'cover'
                ? {
                    objectFit: 'cover',
                    // hack to make the video player fill the entire container
                    width: 'calc(100% + 1px)',
                    height: 'calc(100% + 1px)',
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                  }
                : { objectFit: 'contain', width: '100%', height: '100%' }),
              ...this.backgroundStyles(),
              borderRadius: attributes.borderRadius,
            },
            controlslist: 'nodownload',
          },
        },
      },
      width: '100%',
      height: '100%',
      style: {
        zIndex: 0,
        opacity: !this.state.layout || thumbnail || placeholder ? 0 : 1,
      },
      stopOnUnmount: true,
      // prevents video from going fullscreen on iOS when the player is clicked
      playsinline: true,
      url: this.getURL(),
      onError: () => this.setState({ videoLoadError: true }),
      // video player options
      playing: false,
      muted: false,
      loop: false,
      controls: true,
      ...(attributes.videoMuted === true && { muted: true }),
      ...(attributes.videoLoop === true && { loop: true }),
      ...(attributes.videoAutoPlay === true && { playing: true, muted: true }),
      ...(attributes.videoControls === true && { controls: true }),
      ...(attributes.videoControls === false && {
        controls: false,
        playing: true,
        muted: true,
      }),
      ...(this.state.clickedPlay === true && { playing: true }),
    }

    // Show the player when:
    // - layout is available
    // - play button has been clicked OR video auto play is true
    const player =
      this.state.layout &&
      (this.state.clickedPlay === true || attributes.videoAutoPlay === true) &&
      this.state.videoLoadError === false ? (
        <ReactPlayer {...props} />
      ) : null

    return (
      <View style={wrapperStyles} onLayout={this.onLayout}>
        <View style={[styles.background, backgroundStyles]}>
          <View
            style={[
              styles.backgroundOverlay,
              {
                borderRadius: this.getBorderRadius(true),
              },
            ]}
          >
            <View style={backgroundOverlayInnerStyles} />
          </View>
        </View>
        <View style={innerWrapperStyles}>
          {player}
          {thumbnail}
          {placeholder}
        </View>
      </View>
    )
  }
}

const styles = StyleSheet.create({
  background: {
    position: 'absolute',
  },
  backgroundOverlay: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
  },
  pressableContainer: {
    height: '100%',
  },
  thumbnailContainer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
  },
  thumbnailImage: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 0,
  },
  thumbnailPlayButtonContainer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbnailPlayButton: {
    maxWidth: '100%',
    width: 75,
    height: 75,
    position: 'absolute',
  },
  placeholderImage: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 2,
  },
})

const mapStateToProps = (state, props) => {
  const {
    object,
    getApp,
    getBinding,
    getParams,
    getImageUploadsBaseURL,
    getFileUploadsBaseURL,
    listItemId,
    topScreen,
  } = props

  if (object.attributes.videoType === 'url') {
    const app = getApp && getApp()

    const { width, height } = object.attributes

    const fakeObject = {
      attributes: {
        video: { type: 'binding', source: object.attributes.videoURL },
        image:
          object.attributes.imageType === 'url'
            ? { type: 'binding', source: object.attributes.imageURL }
            : object.attributes.imageBinding,
        width,
        height,
        listItemId,
      },
      libraryComponentManifest,
    }

    return {
      ...solveBindings(app, fakeObject, {
        state,
        getBinding,
        getParams,
        getImageUploadsBaseURL,
        getFileUploadsBaseURL,
      }),
      topScreen,
    }
  }

  throw new Error('uploaded videos not supported, yet')
}

const mapDispatchToProps = dispatch => {
  return {
    addImageLoaded: url => dispatch(addImageLoaded(url)),
  }
}

const ConnectedVideoView = connect(
  mapStateToProps,
  mapDispatchToProps
)(VideoView)

export default class WrappedVideoView extends Component {
  static contextType = AppContext

  render() {
    const { getApp, getParams, getImageUploadsBaseURL, getAssetURL } =
      this.context

    const { getBinding } = createBindingUtils(this.props, this.context)

    return (
      <ConnectedVideoView
        {...this.props}
        getApp={getApp}
        getBinding={getBinding}
        getParams={getParams}
        getImageUploadsBaseURL={getImageUploadsBaseURL}
        getAssetURL={getAssetURL}
      />
    )
  }
}
